import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Grid, Skeleton, useTheme } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import CoursesCard from 'src/components/molecules/courseCard/card';

import { CourseSectionTabs, StyleTab, Title } from './style';

import { getNamespaces } from '@helpers/i18nUtils';
import { useBreakpoint, useDegreeCourse } from '@helpers/use';
import { DegreeCourseLandingPageLabels } from '@interfaces/degree';
import { DegreeCourseType } from '@interfaces/university';

export enum ActiveTabEnum {
  ALL = '',
  MANDATORY = 'Mandatory',
  ELECTIVE = 'Elective',
  ELECTIVE_1 = 'Elective_1',
  ELECTIVE_2 = 'Elective_2',
}

const ELECTIVE_GROUP_E1 = 1;
const ELECTIVE_GROUP_E2 = 2;

export const getDegreeCourseQueryParams = (activeTab: ActiveTabEnum): Record<string, unknown> => {
  switch (activeTab) {
    case ActiveTabEnum.MANDATORY:
      return { degreeCourseType: DegreeCourseType.MANDATORY };
    case ActiveTabEnum.ELECTIVE:
      return { degreeCourseType: DegreeCourseType.ELECTIVE };
    case ActiveTabEnum.ELECTIVE_1:
      return { degreeCourseType: DegreeCourseType.ELECTIVE, electiveGroup: ELECTIVE_GROUP_E1 };
    case ActiveTabEnum.ELECTIVE_2:
      return { degreeCourseType: DegreeCourseType.ELECTIVE, electiveGroup: ELECTIVE_GROUP_E2 };
    default:
      return {};
  }
};

const hasElectiveE2 = (electiveGroupQty?: number) => (electiveGroupQty ?? 1) > 1;

type CourseTab = {
  id: string;
  label: string;
  value: ActiveTabEnum;
};

export const buildCourseTabs = ({
  isHasE2,
  labels,
  t,
}: {
  isHasE2: boolean;
  labels?: DegreeCourseLandingPageLabels;
  t: (key: string) => string;
}): CourseTab[] => {
  const tabs: CourseTab[] = [
    {
      id: 'courses-tab-all',
      label: t('degree_landing_page.course_section.tabs.all_course'),
      value: ActiveTabEnum.ALL,
    },
    {
      id: 'courses-tab-mandatory',
      label: labels?.mandatory || t('degree_landing_page.course_section.tabs.mandatory'),
      value: ActiveTabEnum.MANDATORY,
    },
  ];

  if (isHasE2) {
    tabs.push(
      {
        id: 'courses-tab-elective-1',
        label: labels?.elective1 || t('degree_landing_page.course_section.tabs.elective1'),
        value: ActiveTabEnum.ELECTIVE_1,
      },
      {
        id: 'courses-tab-elective-2',
        label: labels?.elective2 || t('degree_landing_page.course_section.tabs.elective2'),
        value: ActiveTabEnum.ELECTIVE_2,
      },
    );
  } else {
    tabs.push({
      id: 'courses-tab-elective',
      label: t('degree_landing_page.course_section.tabs.elective'),
      value: ActiveTabEnum.ELECTIVE,
    });
  }

  return tabs;
};

export const CoursesSection = () => {
  const theme = useTheme();
  const { isMobile } = useBreakpoint();
  const [activeTab, setActiveTab] = useState<ActiveTabEnum>(ActiveTabEnum.ALL);

  const router = useRouter();
  const degreeIdName = (router.query.degreeIdName as string) || '';
  const { t } = useTranslation(getNamespaces({ namespaces: [degreeIdName] }));

  const degreeId = router.query.degreeIdName as string;
  const queryParams = useMemo(() => getDegreeCourseQueryParams(activeTab), [activeTab]);
  const { courses, electiveGroupQty, labels, isLoading } = useDegreeCourse(degreeId, queryParams);

  const labelsRef = useRef<DegreeCourseLandingPageLabels>();
  const electiveGroupQtyRef = useRef<number>();

  if (labels) {
    labelsRef.current = labels;
  }
  if (electiveGroupQty !== undefined) {
    electiveGroupQtyRef.current = electiveGroupQty;
  }

  const stableLabels = labels ?? labelsRef.current;
  const stableElectiveGroupQty = electiveGroupQty ?? electiveGroupQtyRef.current;

  const showElectiveE2Tabs = hasElectiveE2(stableElectiveGroupQty);
  const courseTabs = useMemo(
    () => buildCourseTabs({ isHasE2: showElectiveE2Tabs, labels: stableLabels, t }),
    [stableLabels, showElectiveE2Tabs, t],
  );

  useEffect(() => {
    if (!showElectiveE2Tabs || activeTab !== ActiveTabEnum.ELECTIVE) {
      return;
    }

    setActiveTab(ActiveTabEnum.ELECTIVE_1);
  }, [activeTab, showElectiveE2Tabs]);

  const onChange = (_event: React.SyntheticEvent, newValue: ActiveTabEnum) => {
    setActiveTab(newValue);
  };

  return (
    <Box
      mb={{
        xs: 8,
        md: 10,
      }}
    >
      <Title>{t('degree_landing_page.course_section.title')}</Title>
      <Box display="flex" flexDirection="column" alignItems="center">
        <Box
          sx={{
            width: '100%',
            borderBottom: 1,
            borderColor: 'divider',
            mb: 5,
            [theme.breakpoints.up('sm')]: {
              width: showElectiveE2Tabs ? '100%' : 536,
            },
          }}
        >
          <CourseSectionTabs
            className={clsx({ multiline: showElectiveE2Tabs })}
            variant={showElectiveE2Tabs ? 'scrollable' : isMobile ? 'fullWidth' : 'standard'}
            scrollButtons={false}
            value={activeTab}
            onChange={onChange}
            centered={!showElectiveE2Tabs}
          >
            {courseTabs.map((tab) => (
              <StyleTab key={tab.id} id={tab.id} label={tab.label} value={tab.value} />
            ))}
          </CourseSectionTabs>
        </Box>
        <Box ml={-1} width="100%">
          <Grid container spacing={1}>
            {(isLoading ? Array.from(new Array(12)) : courses).map((course) => (
              <Grid item xs={12} lg={3} key={course ? `course-${course.id}` : undefined}>
                {course ? (
                  <CoursesCard course={course} />
                ) : (
                  <Skeleton
                    sx={{
                      borderRadius: '8px',
                      height: 80,
                      [theme.breakpoints.up('md')]: {
                        height: 173,
                      },
                      [theme.breakpoints.up('lg')]: {
                        height: 370,
                      },
                    }}
                    variant="rectangular"
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};
